import { useState, useEffect } from 'react';
import { Button, Card, Badge, Input, NumberInput, Icon } from '@atoms/index';
import { AlertModal } from '@organisms/index';
import { restaurantService } from '@services/restaurantService';
import { menuService } from '@services/menuService';
import { formatCurrency, parseDecimal } from '@utils/currency';
import { cn } from '@/utils/cn';
import type { AlertModalVariant } from './AlertModal';

interface ModifierGroupManagementProps {
  onGroupCreated?: (group: any) => void;
  onGroupDuplicated?: (group: any) => void;
}

export const ModifierGroupManagement = ({
  onGroupCreated,
  onGroupDuplicated,
}: ModifierGroupManagementProps) => {
  const [modifierGroups, setModifierGroups] = useState<any[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [showCreateForm, setShowCreateForm] = useState(false);
  const [expandedGroups, setExpandedGroups] = useState<Set<string>>(new Set());
  const [editingGroupId, setEditingGroupId] = useState<string | null>(null);
  const [editingOptionId, setEditingOptionId] = useState<string | null>(null);
  const [showOptionForm, setShowOptionForm] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [alertState, setAlertState] = useState<{ isOpen: boolean; title: string; message: string; variant: AlertModalVariant }>({ isOpen: false, title: '', message: '', variant: 'warning' });

  useEffect(() => {
    loadModifierGroups();
  }, []);

  const loadModifierGroups = async () => {
    setIsLoading(true);
    setError(null);
    try {
      const groups = await menuService.getModifierGroups();
      setModifierGroups(groups);
      setExpandedGroups(new Set(groups.map((g) => g.id)));
    } catch (err: any) {
      setError(err.response?.data?.error?.message || 'Yan ürün grupları yüklenirken hata oluştu');
    } finally {
      setIsLoading(false);
    }
  };

  const toggleGroupExpanded = (groupId: string) => {
    setExpandedGroups((prev) => {
      const next = new Set(prev);
      if (next.has(groupId)) {
        next.delete(groupId);
      } else {
        next.add(groupId);
      }
      return next;
    });
  };

  const handleCreate = async (formData: any) => {
    try {
      const payload = {
        ...formData,
        modifierOptions: formData.modifierOptions.map((o: any) => ({
          ...o,
          priceAdjustment: parseDecimal(o.priceAdjustment),
        })),
      };
      const created = await restaurantService.createModifierGroup(payload);
      await loadModifierGroups();
      setShowCreateForm(false);
      onGroupCreated?.(created);
    } catch (err: any) {
      setError(err.response?.data?.error?.message || 'Yan ürün grubu oluşturulamadı');
      throw err;
    }
  };

  const handleUpdate = async (groupId: string, formData: any) => {
    try {
      const payload = {
        name: formData.name,
        description: formData.description?.trim() || null,
        minSelection: formData.minSelection,
        maxSelection: formData.maxSelection,
        isRequired: formData.isRequired,
        displayOrder: formData.displayOrder ?? 0,
      };
      await menuService.updateModifierGroup(groupId, payload);
      await loadModifierGroups();
      setEditingGroupId(null);
    } catch (err: any) {
      setError(err.response?.data?.error?.message || 'Yan ürün grubu güncellenemedi');
      throw err;
    }
  };

  const handleDelete = async (groupId: string) => {
    if (!confirm('Bu yan ürün grubunu silmek istediğinize emin misiniz? Tüm seçenekler de silinecektir.')) {
      return;
    }

    try {
      await menuService.deleteModifierGroup(groupId);
      await loadModifierGroups();
    } catch (err: any) {
      setError(err.response?.data?.error?.message || 'Yan ürün grubu silinemedi');
    }
  };

  const handleDuplicate = async (groupId: string) => {
    try {
      const duplicated = await restaurantService.duplicateModifierGroup(groupId);
      await loadModifierGroups();
      onGroupDuplicated?.(duplicated);
    } catch (err: any) {
      setError(err.response?.data?.error?.message || 'Yan ürün grubu kopyalanamadı');
    }
  };

  const handleCreateOption = async (modifierGroupId: string, formData: any) => {
    try {
      await menuService.createModifierOption(modifierGroupId, {
        ...formData,
        priceAdjustment: parseDecimal(formData.priceAdjustment),
      });
      await loadModifierGroups();
      setShowOptionForm(null);
    } catch (err: any) {
      setError(err.response?.data?.error?.message || 'Seçenek oluşturulamadı');
      throw err;
    }
  };

  const handleUpdateOption = async (optionId: string, formData: any) => {
    try {
      await menuService.updateModifierOption(optionId, {
        ...formData,
        priceAdjustment: parseDecimal(formData.priceAdjustment),
      });
      await loadModifierGroups();
      setEditingOptionId(null);
    } catch (err: any) {
      setError(err.response?.data?.error?.message || 'Seçenek güncellenemedi');
      throw err;
    }
  };

  const handleDeleteOption = async (optionId: string) => {
    if (!confirm('Bu seçeneği silmek istediğinize emin misiniz?')) {
      return;
    }

    try {
      await menuService.deleteModifierOption(optionId);
      await loadModifierGroups();
    } catch (err: any) {
      setError(err.response?.data?.error?.message || 'Seçenek silinemedi');
    }
  };

  if (isLoading) {
    return (
      <Card padding="lg">
        <div className="flex flex-col items-center justify-center py-12">
          <div className="w-10 h-10 rounded-2xl border-2 border-primary/30 border-t-primary animate-spin mb-4" />
          <p className="text-gray-500 font-medium">Yükleniyor...</p>
        </div>
      </Card>
    );
  }

  return (
    <div className="space-y-4">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <Button
          variant="primary"
          size="sm"
          onClick={() => setShowCreateForm(!showCreateForm)}
          className="rounded-xl font-semibold shadow-sm w-full sm:w-auto"
        >
          <Icon name={showCreateForm ? 'close' : 'plus'} size={18} className="mr-2" />
          {showCreateForm ? 'İptal' : 'Yeni Grup'}
        </Button>
      </div>

      {error && (
        <Card className="bg-error/5 border-error/20">
          <div className="flex items-start justify-between gap-4">
            <p className="text-error text-sm font-medium flex-1">{error}</p>
            <Button size="sm" variant="secondary" onClick={() => setError(null)} className="flex-shrink-0 rounded-xl">
              Kapat
            </Button>
          </div>
        </Card>
      )}

      {showCreateForm && (
        <Card padding="lg" className="border-2 border-primary/20">
          <ModifierGroupForm
            onSubmit={handleCreate}
            onCancel={() => setShowCreateForm(false)}
          />
        </Card>
      )}

      <div className="space-y-4">
        {modifierGroups.length === 0 ? (
          <Card padding="lg" className="text-center">
            <div className="py-12">
              <div className="w-14 h-14 rounded-2xl bg-gray-100 flex items-center justify-center mx-auto mb-4">
                <Icon name="list" size={28} className="text-gray-400" />
              </div>
              <p className="font-semibold text-gray-700 mb-1">Henüz yan ürün grubu yok</p>
              <p className="text-sm text-gray-500 mb-6 max-w-md mx-auto">
                Sos seçimi, ekstra malzemeler gibi gruplar oluşturup birden fazla ürüne atayabilirsiniz.
              </p>
              <div className="text-left max-w-lg mx-auto space-y-3 text-sm mb-6">
                <div className="flex gap-3 p-3 rounded-xl bg-gray-50 border border-gray-100">
                  <span className="flex-shrink-0 w-6 h-6 rounded-lg bg-primary/10 text-primary font-bold flex items-center justify-center">1</span>
                  <div>
                    <span className="font-medium text-gray-800">Yan ürünler:</span>
                    <p className="text-gray-600">Patates, Pilav, Salata → Ana yemeklere eklenebilir</p>
                  </div>
                </div>
                <div className="flex gap-3 p-3 rounded-xl bg-gray-50 border border-gray-100">
                  <span className="flex-shrink-0 w-6 h-6 rounded-lg bg-primary/10 text-primary font-bold flex items-center justify-center">2</span>
                  <div>
                    <span className="font-medium text-gray-800">Sos seçimi:</span>
                    <p className="text-gray-600">BBQ, Ketçap, Mayonez → Hamburger, tavuk vb.</p>
                  </div>
                </div>
                <div className="flex gap-3 p-3 rounded-xl bg-gray-50 border border-gray-100">
                  <span className="flex-shrink-0 w-6 h-6 rounded-lg bg-primary/10 text-primary font-bold flex items-center justify-center">3</span>
                  <div>
                    <span className="font-medium text-gray-800">Ekstralar:</span>
                    <p className="text-gray-600">Ekstra peynir, et → İstediğiniz ürünlere</p>
                  </div>
                </div>
              </div>
              <Button variant="primary" size="sm" onClick={() => setShowCreateForm(true)} className="rounded-xl">
                <Icon name="plus" size={18} className="mr-2" />
                İlk Grubu Oluştur
              </Button>
            </div>
          </Card>
        ) : (
          modifierGroups.map((group) => (
            <ModifierGroupCard
              key={group.id}
              group={group}
              isExpanded={expandedGroups.has(group.id)}
              onToggleExpanded={() => toggleGroupExpanded(group.id)}
              isEditing={editingGroupId === group.id}
              onStartEdit={() => setEditingGroupId(group.id)}
              onCancelEdit={() => setEditingGroupId(null)}
              onSave={(formData) => handleUpdate(group.id, formData)}
              onDelete={() => handleDelete(group.id)}
              onDuplicate={() => handleDuplicate(group.id)}
              showOptionForm={showOptionForm === group.id}
              onShowOptionForm={() => setShowOptionForm(group.id)}
              onHideOptionForm={() => setShowOptionForm(null)}
              onCreateOption={(formData) => handleCreateOption(group.id, formData)}
              editingOptionId={editingOptionId}
              onStartEditOption={setEditingOptionId}
              onCancelEditOption={() => setEditingOptionId(null)}
              onUpdateOption={handleUpdateOption}
              onDeleteOption={handleDeleteOption}
              onAlert={(title, message, variant) => setAlertState({ isOpen: true, title, message, variant })}
            />
          ))
        )}
      </div>
      <AlertModal
        isOpen={alertState.isOpen}
        title={alertState.title}
        message={alertState.message}
        variant={alertState.variant}
        onClose={() => setAlertState((a) => ({ ...a, isOpen: false }))}
      />
    </div>
  );
};

interface ModifierGroupFormProps {
  onSubmit: (formData: any) => Promise<void>;
  onCancel: () => void;
  initialData?: any;
  onAlert?: (title: string, message: string, variant: AlertModalVariant) => void;
}

const ModifierGroupForm = ({ onSubmit, onCancel, initialData, onAlert }: ModifierGroupFormProps) => {
  const isEditMode = Boolean(initialData?.id);
  const [formData, setFormData] = useState({
    name: initialData?.name || '',
    description: initialData?.description || '',
    minSelection: initialData?.minSelection ?? 0,
    maxSelection: initialData?.maxSelection ?? null as number | null,
    isRequired: initialData?.isRequired || false,
    displayOrder: initialData?.displayOrder || 0,
    modifierOptions: (initialData?.modifierOptions || []).map((o: any) => ({
      ...o,
      priceAdjustment: o.priceAdjustment != null ? String(o.priceAdjustment) : '',
    })) as Array<{
      name: string;
      description?: string;
      priceAdjustment: string;
      calories?: number;
      displayOrder?: number;
      isDefault?: boolean;
    }>,
  });
  const [isSubmitting, setIsSubmitting] = useState(false);

  const addModifierOption = () => {
    setFormData((prev) => ({
      ...prev,
      modifierOptions: [
        ...prev.modifierOptions,
        {
          name: '',
          priceAdjustment: '',
          displayOrder: prev.modifierOptions.length,
          isDefault: false,
        },
      ],
    }));
  };

  const removeModifierOption = (index: number) => {
    setFormData((prev) => ({
      ...prev,
      modifierOptions: prev.modifierOptions.filter((_, i) => i !== index),
    }));
  };

  const updateModifierOption = (index: number, field: string, value: any) => {
    setFormData((prev) => ({
      ...prev,
      modifierOptions: prev.modifierOptions.map((opt, i) =>
        i === index ? { ...opt, [field]: value } : opt,
      ),
    }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!formData.name.trim()) {
      onAlert?.('Eksik bilgi', 'Grup adı zorunludur', 'warning');
      return;
    }
    if (formData.isRequired && formData.minSelection < 1) {
      onAlert?.('Geçersiz ayar', 'Zorunlu gruplar için minimum seçim en az 1 olmalıdır', 'warning');
      return;
    }
    if (formData.maxSelection !== null && formData.maxSelection < formData.minSelection) {
      onAlert?.('Geçersiz ayar', 'Maksimum seçim minimum seçimden küçük olamaz', 'warning');
      return;
    }
    setIsSubmitting(true);
    try {
      await onSubmit(formData);
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-4">
      <div>
        <label className="block text-sm font-semibold text-gray-700 mb-1.5">Grup Adı *</label>
        <Input
          value={formData.name}
          onChange={(e) => setFormData({ ...formData, name: e.target.value })}
          placeholder="Örn: Sos Seçimi"
          required
          className="rounded-xl border-gray-200"
        />
      </div>
      <div>
        <label className="block text-sm font-semibold text-gray-700 mb-1.5">Açıklama</label>
        <textarea
          value={formData.description}
          onChange={(e) => setFormData({ ...formData, description: e.target.value })}
          className="w-full px-4 py-2.5 border border-gray-200 rounded-xl focus:ring-2 focus:ring-primary/20 focus:border-primary bg-white"
          rows={2}
          placeholder="Grup açıklaması (isteğe bağlı)"
        />
      </div>
      <div className="grid grid-cols-2 gap-4">
        <div>
          <label className="block text-sm font-semibold text-gray-700 mb-1.5">Min. Seçim</label>
          <NumberInput
            integerOnly
            value={formData.minSelection === 0 ? '' : String(formData.minSelection)}
            onChange={(s) =>
              setFormData({ ...formData, minSelection: s === '' ? 0 : Math.max(0, parseInt(s, 10) || 0) })
            }
            required
            className="rounded-xl"
          />
        </div>
        <div>
          <label className="block text-sm font-semibold text-gray-700 mb-1.5">Max. Seçim (boş = sınırsız)</label>
          <NumberInput
            integerOnly
            value={formData.maxSelection != null ? String(formData.maxSelection) : ''}
            onChange={(s) =>
              setFormData({
                ...formData,
                maxSelection: s === '' ? null : Math.max(0, parseInt(s, 10) || 0),
              })
            }
            className="rounded-xl"
          />
        </div>
      </div>
      <label className="flex items-center gap-3 cursor-pointer">
        <input
          type="checkbox"
          checked={formData.isRequired}
          onChange={(e) => setFormData({ ...formData, isRequired: e.target.checked })}
          className="w-4 h-4 text-primary border-gray-300 rounded focus:ring-primary"
        />
        <span className="text-sm font-medium text-gray-700">Zorunlu (müşteri seçim yapmalı)</span>
      </label>
      {!isEditMode && (
        <div>
          <div className="flex items-center justify-between mb-2">
            <label className="block text-sm font-semibold text-gray-700">Seçenekler</label>
            <Button variant="secondary" size="sm" onClick={addModifierOption} className="rounded-xl">
              <Icon name="plus" size={14} className="mr-1.5" />
              Seçenek Ekle
            </Button>
          </div>
          <div className="space-y-3">
            {formData.modifierOptions.map((option, index) => (
              <div
                key={index}
                className="p-4 bg-white border border-gray-200 rounded-xl space-y-3 shadow-sm"
              >
                <div className="flex items-center gap-2">
                  <Input
                    value={option.name}
                    onChange={(e) => updateModifierOption(index, 'name', e.target.value)}
                    placeholder="Seçenek adı"
                    className="flex-1 rounded-xl"
                    size="sm"
                  />
                  <Button
                    variant="danger"
                    size="sm"
                    onClick={() => removeModifierOption(index)}
                    className="rounded-xl flex-shrink-0"
                  >
                    <Icon name="trash" size={14} />
                  </Button>
                </div>
                <div className="grid grid-cols-2 gap-2">
                  <NumberInput
                    value={option.priceAdjustment}
                    onChange={(s) => updateModifierOption(index, 'priceAdjustment', s)}
                    placeholder="Fiyat (₺)"
                    className="rounded-lg py-2 text-sm"
                  />
                  <label className="flex items-center gap-2 text-sm text-gray-700">
                    <input
                      type="checkbox"
                      checked={option.isDefault}
                      onChange={(e) =>
                        updateModifierOption(index, 'isDefault', e.target.checked)
                      }
                      className="w-4 h-4 text-primary border-gray-300 rounded focus:ring-primary"
                    />
                    Varsayılan
                  </label>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
      <div className="flex gap-3 justify-end pt-2">
        <Button type="button" variant="secondary" size="sm" onClick={onCancel} className="rounded-xl">
          İptal
        </Button>
        <Button
          type="submit"
          variant="primary"
          size="sm"
          disabled={isSubmitting || !formData.name.trim()}
          className="rounded-xl font-semibold"
        >
          {isSubmitting
            ? isEditMode
              ? 'Kaydediliyor...'
              : 'Oluşturuluyor...'
            : isEditMode
              ? 'Kaydet'
              : 'Grup Oluştur'}
        </Button>
      </div>
    </form>
  );
};

interface ModifierGroupCardProps {
  group: any;
  isExpanded: boolean;
  onToggleExpanded: () => void;
  isEditing: boolean;
  onStartEdit: () => void;
  onCancelEdit: () => void;
  onSave: (formData: any) => Promise<void>;
  onDelete: () => void;
  onDuplicate: () => void;
  showOptionForm: boolean;
  onShowOptionForm: () => void;
  onHideOptionForm: () => void;
  onCreateOption: (formData: any) => Promise<void>;
  editingOptionId: string | null;
  onStartEditOption: (id: string) => void;
  onCancelEditOption: () => void;
  onUpdateOption: (id: string, formData: any) => Promise<void>;
  onDeleteOption: (id: string) => void;
  onAlert?: (title: string, message: string, variant: AlertModalVariant) => void;
}

const ModifierGroupCard = ({
  group,
  isExpanded,
  onToggleExpanded,
  isEditing,
  onStartEdit,
  onCancelEdit,
  onSave,
  onDelete,
  onDuplicate,
  showOptionForm,
  onShowOptionForm,
  onHideOptionForm,
  onCreateOption,
  editingOptionId,
  onStartEditOption,
  onCancelEditOption,
  onUpdateOption,
  onDeleteOption,
  onAlert,
}: ModifierGroupCardProps) => {
  if (isEditing) {
    return (
      <Card padding="lg" className="border-2 border-primary/30 rounded-2xl">
        <ModifierGroupForm
          initialData={group}
          onSubmit={onSave}
          onCancel={onCancelEdit}
          onAlert={onAlert}
        />
      </Card>
    );
  }

  return (
    <Card padding="md" className="rounded-2xl overflow-hidden transition-all duration-300 hover:shadow-card-hover">
      <div
        className={cn(
          'transition-all duration-200',
          isExpanded && 'border-b border-gray-100 pb-4 mb-4',
        )}
      >
        <div className="flex items-center gap-3">
          <button
            type="button"
            onClick={onToggleExpanded}
            className="flex-shrink-0 w-9 h-9 rounded-xl bg-gray-100 hover:bg-gray-200 flex items-center justify-center text-gray-600 transition-colors"
          >
            <Icon
              name="chevron-down"
              size={18}
              className={cn('transition-transform duration-200', !isExpanded && '-rotate-90')}
            />
          </button>
          <button
            type="button"
            onClick={onToggleExpanded}
            className="flex-1 flex flex-col items-start text-left min-w-0"
          >
            <div className="flex flex-wrap items-center gap-2">
              <h4 className="font-bold text-gray-900 group-hover:text-primary transition-colors">
                {group.name}
              </h4>
              {group.isRequired && (
                <Badge variant="primary" className="text-xs rounded-lg">
                  Zorunlu
                </Badge>
              )}
              {!group.isActive && (
                <Badge variant="danger" className="text-xs rounded-lg">
                  Pasif
                </Badge>
              )}
              <Badge variant="secondary" className="text-xs rounded-lg">
                {group.modifierOptions?.length || 0} seçenek
              </Badge>
            </div>
            {group.description && (
              <p className="text-xs text-gray-500 mt-0.5 line-clamp-1">{group.description}</p>
            )}
            <p className="text-xs text-gray-500 mt-1">
              Min: {group.minSelection} • Max: {group.maxSelection ?? '∞'}
            </p>
          </button>
          <div className="flex items-center gap-1 flex-shrink-0">
            <button
              type="button"
              onClick={(e) => {
                e.stopPropagation();
                onStartEdit();
              }}
              className="p-2.5 hover:bg-primary/10 rounded-xl text-gray-600 hover:text-primary transition-colors"
              title="Düzenle"
            >
              <Icon name="pencil" size={16} />
            </button>
            <button
              type="button"
              onClick={(e) => {
                e.stopPropagation();
                onDuplicate();
              }}
              className="p-2.5 hover:bg-primary/10 rounded-xl text-gray-600 hover:text-primary transition-colors"
              title="Kopyala"
            >
              <Icon name="copy" size={16} />
            </button>
            <button
              type="button"
              onClick={(e) => {
                e.stopPropagation();
                onDelete();
              }}
              className="p-2.5 hover:bg-error/10 rounded-xl text-gray-600 hover:text-error transition-colors"
              title="Sil"
            >
              <Icon name="trash" size={16} />
            </button>
          </div>
        </div>
      </div>

      {isExpanded && (
        <div className="space-y-4">
          <div className="flex items-center justify-between">
            <h5 className="text-sm font-semibold text-gray-700">Seçenekler</h5>
            <Button
              variant="secondary"
              size="sm"
              onClick={showOptionForm ? onHideOptionForm : onShowOptionForm}
              className="rounded-xl"
            >
              <Icon name={showOptionForm ? 'close' : 'plus'} size={14} className="mr-1.5" />
              {showOptionForm ? 'İptal' : 'Seçenek Ekle'}
            </Button>
          </div>

          {showOptionForm && (
            <div className="rounded-xl border-2 border-primary/20 p-4 bg-gray-50/50">
              <ModifierOptionForm
                onSubmit={onCreateOption}
                onCancel={onHideOptionForm}
                onValidationError={(message) => onAlert?.('Eksik bilgi', message, 'warning')}
              />
            </div>
          )}

          <div className="space-y-2">
            {group.modifierOptions && group.modifierOptions.length > 0 ? (
              group.modifierOptions.map((option: any) => (
                <ModifierOptionItem
                  key={option.id}
                  option={option}
                  isEditing={editingOptionId === option.id}
                  onStartEdit={() => onStartEditOption(option.id)}
                  onCancelEdit={onCancelEditOption}
                  onSave={(formData) => onUpdateOption(option.id, formData)}
                  onDelete={() => onDeleteOption(option.id)}
                  onValidationError={(message) => onAlert?.('Eksik bilgi', message, 'warning')}
                />
              ))
            ) : (
              <div className="text-center py-6 text-gray-500 text-sm border-2 border-dashed border-gray-200 rounded-xl bg-gray-50/50">
                Henüz seçenek yok. Seçenek ekleyin.
              </div>
            )}
          </div>
        </div>
      )}
    </Card>
  );
};

interface ModifierOptionFormProps {
  onSubmit: (formData: any) => Promise<void>;
  onCancel: () => void;
  initialData?: any;
  onValidationError?: (message: string) => void;
}

const ModifierOptionForm = ({ onSubmit, onCancel, initialData, onValidationError }: ModifierOptionFormProps) => {
  const [formData, setFormData] = useState({
    name: initialData?.name || '',
    description: initialData?.description || '',
    priceAdjustment: initialData?.priceAdjustment != null ? String(initialData.priceAdjustment) : '',
    calories: initialData?.calories ?? (null as number | null),
    displayOrder: initialData?.displayOrder || 0,
    isDefault: initialData?.isDefault || false,
  });
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!formData.name.trim()) {
      onValidationError?.('Seçenek adı zorunludur.');
      return;
    }
    setIsSubmitting(true);
    try {
      await onSubmit(formData);
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-3">
      <div className="grid grid-cols-2 gap-3">
        <div>
          <label className="block text-xs font-semibold text-gray-700 mb-1">Seçenek Adı *</label>
          <Input
            value={formData.name}
            onChange={(e) => setFormData({ ...formData, name: e.target.value })}
            placeholder="Örn: BBQ Sos"
            required
            size="sm"
            className="rounded-xl"
          />
        </div>
        <div>
          <label className="block text-xs font-semibold text-gray-700 mb-1">Fiyat (₺)</label>
          <NumberInput
            value={formData.priceAdjustment}
            onChange={(s) => setFormData({ ...formData, priceAdjustment: s })}
            placeholder="0,00"
            className="rounded-xl py-2 text-sm"
          />
        </div>
      </div>
      <div>
        <label className="block text-xs font-semibold text-gray-700 mb-1">Açıklama</label>
        <Input
          value={formData.description}
          onChange={(e) => setFormData({ ...formData, description: e.target.value })}
          placeholder="İsteğe bağlı"
          size="sm"
          className="rounded-xl"
        />
      </div>
      <label className="flex items-center gap-2 text-xs text-gray-700">
        <input
          type="checkbox"
          checked={formData.isDefault}
          onChange={(e) => setFormData({ ...formData, isDefault: e.target.checked })}
          className="w-4 h-4 text-primary border-gray-300 rounded focus:ring-primary"
        />
        Varsayılan seçim
      </label>
      <div className="flex gap-2 justify-end pt-1">
        <Button type="button" variant="secondary" size="sm" onClick={onCancel} className="rounded-xl">
          İptal
        </Button>
        <Button type="submit" variant="primary" size="sm" disabled={isSubmitting} className="rounded-xl">
          {isSubmitting ? 'Kaydediliyor...' : 'Kaydet'}
        </Button>
      </div>
    </form>
  );
};

interface ModifierOptionItemProps {
  option: any;
  isEditing: boolean;
  onStartEdit: () => void;
  onCancelEdit: () => void;
  onSave: (formData: any) => Promise<void>;
  onDelete: () => void;
  onValidationError?: (message: string) => void;
}

const ModifierOptionItem = ({
  option,
  isEditing,
  onStartEdit,
  onCancelEdit,
  onSave,
  onDelete,
  onValidationError,
}: ModifierOptionItemProps) => {
  if (isEditing) {
    return (
      <div className="p-4 bg-white border-2 border-primary/20 rounded-xl shadow-sm">
        <ModifierOptionForm
          initialData={option}
          onSubmit={onSave}
          onCancel={onCancelEdit}
          onValidationError={onValidationError}
        />
      </div>
    );
  }

  return (
    <div className="flex items-center justify-between gap-4 p-3 rounded-xl border border-gray-200 bg-white hover:border-gray-300 hover:shadow-sm transition-all">
      <div className="flex-1 min-w-0">
        <div className="flex flex-wrap items-center gap-2">
          <span className="font-semibold text-gray-900">{option.name}</span>
          {option.isDefault && (
            <Badge variant="info" className="text-xs rounded-lg">
              Varsayılan
            </Badge>
          )}
          {!option.isActive && (
            <Badge variant="danger" className="text-xs rounded-lg">
              Pasif
            </Badge>
          )}
        </div>
        {option.description && (
          <p className="text-xs text-gray-500 mt-0.5 line-clamp-1">{option.description}</p>
        )}
      </div>
      <div className="flex items-center gap-2 flex-shrink-0">
        <span className="text-sm font-bold text-primary tabular-nums">
          {option.priceAdjustment > 0 ? '+' : ''}
          {formatCurrency(option.priceAdjustment)}
        </span>
        <button
          type="button"
          onClick={onStartEdit}
          className="p-2 hover:bg-primary/10 rounded-lg text-gray-600 hover:text-primary transition-colors"
          title="Düzenle"
        >
          <Icon name="pencil" size={14} />
        </button>
        <button
          type="button"
          onClick={onDelete}
          className="p-2 hover:bg-error/10 rounded-lg text-gray-600 hover:text-error transition-colors"
          title="Sil"
        >
          <Icon name="trash" size={14} />
        </button>
      </div>
    </div>
  );
};
